import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  exportDealDocuments,
  fetchDealFormsSchemaCached,
  getDealFormsSchemaCache,
} from '../api/dealsClient'
import {
  clearDealsDraft,
  exportFormSummary,
  exportPreviewFilename,
  fieldInputHint,
  formatDraftTime,
  groupVisibleFields,
  loadDealsDraft,
  placeholderForField,
  saveDealsDraft,
  TOGGLE_SCOPE,
  TOGGLE_SCOPE_LABELS,
} from '../utils/deals/dealsPage'

const inputClassBase =
  'w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:ring-2'

const cardClass = 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-5'

const toggleDefault = {
  include_catalog: true,
  redelivery_advance: true,
  include_legal: true,
  include_marketing: true,
  discretionary_marketing: false,
  with_options: false,
  proposal_term_length: false,
  publishing_addendum: false,
}

/** Mirrors api/deals._toggles_to_preset_flags + zDealForms._resolve_effective_preset_flags. */
function effectivePresetFlags(toggles) {
  const t = toggles || {}
  const flags = []
  if (t.include_catalog === false) flags.push('no-catalog')
  else if (t.redelivery_advance === false) flags.push('no-redelivery-advance')
  if (t.include_legal === false) flags.push('no-legal')
  if (t.include_marketing === false) flags.push('no-marketing-budget')
  if (t.discretionary_marketing === true) flags.push('discretionary-marketing')
  if (t.with_options === true) flags.push('with-options')
  if (t.proposal_term_length === true) flags.push('proposal-term-length')
  if (t.publishing_addendum === true) flags.push('publishing-addendum')

  const noRedelivery = flags.includes('no-redelivery-advance')
  let effective = noRedelivery
    ? flags.filter((f) => f !== 'no-redelivery-advance')
    : [...flags]
  const hasCatalog = !effective.includes('no-catalog')
  if (hasCatalog && !noRedelivery && !effective.includes('redelivery-advance')) {
    effective.push('redelivery-advance')
  }
  return effective
}

const LONGFORM_ONLY_FIELDS = new Set([
  'ArtistLegalName',
  'AttorneyName',
  'CatalogTrackNames',
  'EffectiveDate',
  'DiscretionaryMktg',
  'NumOptionTracks',
  'OptionAdvance',
  'OptionExAmount',
  'OptionFinAmount',
])

const FRONTLINE_ONLY_FIELDS = new Set([
  'ArtistEmail',
  'SalutationName',
  'NumOptions',
  'LP2Min',
  'LP2Max',
  'LP3Min',
  'LP3Max',
  'RoyaltyRate',
])

const DISTRIBUTION_ONLY_FIELDS = new Set(['DistroFee', 'MatchLength', 'LicenseLength'])

const DEFAULT_TOGGLE_DEFS = [
  { id: 'include_catalog', label: 'Include Catalog' },
  {
    id: 'redelivery_advance',
    label: 'Redelivery Advance (3-Part)',
    requires_toggle_on: 'include_catalog',
  },
  { id: 'include_legal', label: 'Include Legal Advance' },
  { id: 'include_marketing', label: 'Include Marketing Budget' },
  { id: 'discretionary_marketing', label: 'Discretionary Marketing Fund' },
  { id: 'with_options', label: 'Option Period' },
  { id: 'proposal_term_length', label: 'Include term-length language in proposal' },
  { id: 'publishing_addendum', label: 'Publishing Addendum' },
]

const REQUIRES_TOGGLE_LABEL = {
  include_catalog: 'Include Catalog',
}

function DealCard({ title, children, className = '' }) {
  return (
    <section className={`${cardClass} ${className}`.trim()}>
      {title ? (
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </h2>
      ) : null}
      {children}
    </section>
  )
}

function FormChip({ label, selected, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={[
        'rounded-lg px-4 py-2 text-sm font-medium transition',
        selected
          ? 'bg-gray-900 text-white shadow-sm'
          : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

function OptionToggleRow({ toggleDef, toggles, toggleDefsById, onChange }) {
  const disabled = toggleDef.requires_toggle_on
    ? !toggles[toggleDef.requires_toggle_on]
    : false
  const disabledReason = disabled
    ? `Requires ${REQUIRES_TOGGLE_LABEL[toggleDef.requires_toggle_on] || toggleDef.requires_toggle_on}`
    : null
  const help = toggleDef.help || toggleDefsById[toggleDef.id]?.help

  return (
    <div className="flex flex-col gap-1">
      <label
        className={[
          'flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 text-sm',
          disabled
            ? 'cursor-not-allowed text-gray-400'
            : 'text-gray-800 hover:bg-gray-50',
        ].join(' ')}
        title={disabled ? disabledReason : undefined}
      >
        <input
          type="checkbox"
          className="mt-0.5 rounded border-gray-300 text-[var(--encore-blue)] focus:ring-blue-100"
          checked={Boolean(toggles[toggleDef.id])}
          disabled={disabled}
          onChange={(e) => onChange(toggleDef.id, e.target.checked)}
        />
        <span className="font-medium leading-snug">{toggleDef.label}</span>
      </label>
      {disabled && disabledReason ? (
        <p className="pl-9 text-xs text-gray-400">{disabledReason}</p>
      ) : null}
      {help ? (
        <details className="pl-9 text-xs text-gray-500">
          <summary className="cursor-pointer text-gray-400 hover:text-gray-600">
            What this affects
          </summary>
          <p className="mt-1 leading-relaxed">{help}</p>
        </details>
      ) : null}
    </div>
  )
}

function FieldSkeletonGrid() {
  return (
    <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-4 w-36 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-full max-w-sm animate-pulse rounded bg-gray-100" />
          <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
        </div>
      ))}
    </div>
  )
}

function applyFieldDefaults(data, prev) {
  const next = { ...prev }
  for (const field of data.fields || []) {
    if (next[field.name] == null || next[field.name] === '') {
      if (field.default) next[field.name] = field.default
    }
  }
  return next
}

function isFieldRequired(field) {
  return field?.required !== false
}

function fieldLabel(field, noLegal) {
  const useNoLegal = noLegal && (field.prompt_no_legal || field.help_no_legal)
  return useNoLegal && field.prompt_no_legal ? field.prompt_no_legal : field.prompt
}

function missingRequiredFields(fields, values) {
  return (fields || []).filter(
    (field) => isFieldRequired(field) && !String(values[field.name] ?? '').trim(),
  )
}

/** Show "Show more" inline when help text overflows one line at the current width. */
function FieldHelpText({ id, text }) {
  const textRef = useRef(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const measureOverflow = useCallback(() => {
    const el = textRef.current
    if (!el || expanded) return
    setHasOverflow(el.scrollWidth > el.clientWidth + 1)
  }, [expanded])

  useLayoutEffect(() => {
    setExpanded(false)
    setHasOverflow(false)
  }, [text])

  useLayoutEffect(() => {
    measureOverflow()
  }, [text, measureOverflow])

  useEffect(() => {
    const el = textRef.current
    if (!el) return undefined
    const ro = new ResizeObserver(() => measureOverflow())
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, measureOverflow])

  if (!text) return null

  if (expanded) {
    return (
      <p id={id} className="text-xs leading-relaxed text-gray-500">
        {text}{' '}
        {hasOverflow ? (
          <button
            type="button"
            className="text-[var(--encore-blue)] hover:underline"
            onClick={() => setExpanded(false)}
          >
            Show less
          </button>
        ) : null}
      </p>
    )
  }

  return (
    <div className="flex min-w-0 items-baseline gap-x-1 text-xs text-gray-500">
      <span id={id} ref={textRef} className="min-w-0 flex-1 truncate">
        {text}
      </span>
      {hasOverflow ? (
        <button
          type="button"
          className="shrink-0 text-[var(--encore-blue)] hover:underline"
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      ) : null}
    </div>
  )
}

function DealFieldInput({
  field,
  noLegal,
  value,
  error,
  onChange,
  onResetDefault,
  inputRef,
  inputHint,
}) {
  const useNoLegal = noLegal && (field.prompt_no_legal || field.help_no_legal)
  const label = fieldLabel(field, noLegal)
  const helpRaw = useNoLegal && field.help_no_legal ? field.help_no_legal : field.help
  const required = isFieldRequired(field)
  const isBlank = !String(value ?? '').trim()
  const needsValue = required && isBlank
  const isDefault =
    field.default && String(value ?? '').trim() === String(field.default).trim()
  const helpId = `deal-field-help-${field.name}`
  const errorId = `deal-field-error-${field.name}`

  const inputClass = [
    inputClassBase,
    error
      ? 'border-rose-400 bg-rose-50 focus:border-rose-500 focus:ring-rose-100'
      : needsValue
        ? 'border-rose-300 bg-rose-50/80 focus:border-rose-400 focus:ring-rose-100'
        : 'border-gray-200 focus:border-[var(--encore-blue)] focus:ring-blue-100',
  ].join(' ')

  const commonProps = {
    id: `deal-field-${field.name}`,
    ref: inputRef,
    value: value ?? '',
    onChange,
    placeholder: placeholderForField(field),
    className: inputClass,
    'aria-required': required,
    'aria-invalid': Boolean(error) || needsValue,
    'aria-describedby': [helpRaw ? helpId : null, error ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined,
  }

  return (
    <div
      className={[
        'flex flex-col gap-1.5 rounded-lg',
        needsValue ? '-mx-2 px-2 py-2 ring-1 ring-inset ring-rose-100' : '',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <label
          htmlFor={commonProps.id}
          className={needsValue ? 'text-sm font-medium text-rose-800' : 'text-sm font-medium text-gray-800'}
        >
          {label}
          {required ? (
            <span className="text-rose-600" aria-hidden="true">
              {' '}
              *
            </span>
          ) : null}
        </label>
        {isDefault ? (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Default
          </span>
        ) : null}
        {field.default && !isDefault ? (
          <button
            type="button"
            onClick={onResetDefault}
            className="text-xs text-[var(--encore-blue)] hover:underline"
          >
            Reset to default
          </button>
        ) : null}
      </div>
      {helpRaw ? <FieldHelpText id={helpId} text={helpRaw} /> : null}
      {field.multiline ? (
        <textarea {...commonProps} rows={3} />
      ) : (
        <input type="text" {...commonProps} />
      )}
      {inputHint ? (
        <p className="text-xs text-gray-500">{inputHint}</p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export default function DealsPage() {
  const [toggles, setToggles] = useState(toggleDefault)
  const [forms, setForms] = useState({
    proposal: true,
    longform: true,
    frontline_proposal: false,
  })
  const [schema, setSchema] = useState(() => getDealFormsSchemaCache())
  const [values, setValues] = useState(() => {
    const cached = getDealFormsSchemaCache()
    return cached ? applyFieldDefaults(cached, {}) : {}
  })
  const [loading, setLoading] = useState(() => !getDealFormsSchemaCache())
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [banner, setBanner] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [draftOffer, setDraftOffer] = useState(null)
  const [draftDismissed, setDraftDismissed] = useState(false)

  const fieldRefs = useRef({})
  const draftReadyRef = useRef(false)

  const hasFormSelected =
    forms.proposal || forms.longform || forms.frontline_proposal

  useEffect(() => {
    const draft = loadDealsDraft()
    if (draft?.values && !draftDismissed) {
      setDraftOffer(draft)
    }
  }, [draftDismissed])

  useEffect(() => {
    let cancelled = false
    const cached = getDealFormsSchemaCache()
    if (cached) {
      setSchema(cached)
      setValues((prev) => applyFieldDefaults(cached, prev))
      setLoading(false)
      return undefined
    }
    setLoading(true)
    setError(null)
    fetchDealFormsSchemaCached()
      .then((data) => {
        if (cancelled) return
        setSchema(data)
        setValues((prev) => applyFieldDefaults(data, prev))
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message || 'Failed to load deal form schema')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!draftReadyRef.current) {
      draftReadyRef.current = true
      return undefined
    }
    const timer = window.setTimeout(() => {
      saveDealsDraft({ toggles, forms, values })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [toggles, forms, values])

  const presetFlags = useMemo(() => effectivePresetFlags(toggles), [toggles])
  const presetFlagSet = useMemo(() => new Set(presetFlags), [presetFlags])

  const toggleDefsById = useMemo(() => {
    const defs = schema?.toggles?.length ? schema.toggles : DEFAULT_TOGGLE_DEFS
    return Object.fromEntries(defs.map((t) => [t.id, t]))
  }, [schema])

  const missingTemplates = useMemo(() => {
    const selected = new Set(
      [
        forms.proposal ? 'proposal' : null,
        forms.longform ? 'longform' : null,
        forms.frontline_proposal ? 'frontline_proposal' : null,
      ].filter(Boolean),
    )
    return (schema?.templates || []).filter((t) => {
      if (t.kind === 'publishing_addendum') {
        return toggles.publishing_addendum && forms.longform && !t.available
      }
      return selected.has(t.kind) && !t.available
    })
  }, [schema, forms.proposal, forms.longform, forms.frontline_proposal, toggles.publishing_addendum])

  const visibleFields = useMemo(() => {
    const distroOn = forms.proposal || forms.longform
    return (schema?.fields || []).filter((field) => {
      if (!forms.longform && LONGFORM_ONLY_FIELDS.has(field.name)) return false
      if (
        field.name === 'ArtistAddressAtty' &&
        !forms.longform &&
        !forms.frontline_proposal
      ) {
        return false
      }
      if (!forms.frontline_proposal && FRONTLINE_ONLY_FIELDS.has(field.name)) {
        return false
      }
      if (!distroOn && DISTRIBUTION_ONLY_FIELDS.has(field.name)) return false
      const proposalOnly =
        (forms.proposal || forms.frontline_proposal) && !forms.longform
      if (
        proposalOnly &&
        !presetFlagSet.has('proposal-term-length') &&
        (field.name === 'TermLength' || field.name === 'BufferLength')
      ) {
        return false
      }
      const omit = field.omit_when_presets || []
      if (omit.some((f) => presetFlagSet.has(f))) return false
      if (field.requires_preset && !presetFlagSet.has(field.requires_preset)) return false
      return true
    })
  }, [schema, forms.longform, forms.proposal, forms.frontline_proposal, presetFlagSet])

  const fieldGroups = useMemo(() => groupVisibleFields(visibleFields), [visibleFields])

  const optionToggles = useMemo(() => {
    const defs = schema?.toggles?.length ? schema.toggles : DEFAULT_TOGGLE_DEFS
    return defs.filter((t) => {
      if (
        t.id === 'proposal_term_length' &&
        !forms.proposal &&
        !forms.frontline_proposal
      ) {
        return false
      }
      if (
        (t.id === 'discretionary_marketing' ||
          t.id === 'with_options' ||
          t.id === 'publishing_addendum') &&
        !forms.longform
      ) {
        return false
      }
      return true
    })
  }, [schema, forms.proposal, forms.longform, forms.frontline_proposal])

  const togglesByScope = useMemo(() => {
    const buckets = { distribution: [], longform: [], proposal: [] }
    for (const t of optionToggles) {
      const scope = TOGGLE_SCOPE[t.id] || 'distribution'
      buckets[scope].push(t)
    }
    return buckets
  }, [optionToggles])

  const noLegal = !toggles.include_legal

  const exportDisabledReason = useMemo(() => {
    if (loading) return 'Loading deal forms…'
    if (!hasFormSelected) return 'Select at least one form.'
    if (missingTemplates.length) {
      return 'Deal templates are not configured on this server.'
    }
    return null
  }, [loading, hasFormSelected, missingTemplates.length])

  const exportPreview = useMemo(
    () => exportPreviewFilename(values.ArtistName, forms),
    [values.ArtistName, forms],
  )

  const exportSummary = useMemo(
    () => exportFormSummary(forms, toggles, values),
    [forms, toggles, values],
  )

  const setToggle = useCallback((id, checked) => {
    setToggles((prev) => {
      const next = { ...prev, [id]: checked }
      if (id === 'include_catalog' && !checked) {
        next.redelivery_advance = false
      }
      if (id === 'include_catalog' && checked && prev.redelivery_advance === false) {
        next.redelivery_advance = true
      }
      return next
    })
  }, [])

  function toggleForm(kind) {
    const turningOn = !forms[kind]
    const next = { ...forms, [kind]: turningOn }
    if (turningOn && kind === 'frontline_proposal') {
      next.proposal = false
      next.longform = false
    }
    if (turningOn && (kind === 'proposal' || kind === 'longform')) {
      next.frontline_proposal = false
    }
    if (!next.proposal && !next.longform && !next.frontline_proposal) {
      setBanner({
        tone: 'error',
        text: 'Select at least one form.',
      })
      return
    }
    setBanner(null)
    setFieldErrors({})
    setForms(next)
  }

  function restoreDraft() {
    if (!draftOffer) return
    if (draftOffer.toggles) setToggles({ ...toggleDefault, ...draftOffer.toggles })
    if (draftOffer.forms) setForms({ ...forms, ...draftOffer.forms })
    if (draftOffer.values) {
      setValues((prev) => ({ ...prev, ...draftOffer.values }))
    }
    setDraftOffer(null)
    setDraftDismissed(true)
    setBanner({ tone: 'ok', text: 'Draft restored.' })
  }

  function dismissDraft() {
    clearDealsDraft()
    setDraftOffer(null)
    setDraftDismissed(true)
  }

  function updateField(name, nextValue) {
    setValues((prev) => ({ ...prev, [name]: nextValue }))
    setFieldErrors((prev) => {
      if (!prev[name]) return prev
      const next = { ...prev }
      delete next[name]
      return next
    })
  }

  async function handleExport() {
    const selectedForms = [
      forms.proposal ? 'proposal' : null,
      forms.longform ? 'longform' : null,
      forms.frontline_proposal ? 'frontline_proposal' : null,
    ].filter(Boolean)
    if (!selectedForms.length) {
      setBanner({ tone: 'error', text: 'Select at least one form.' })
      return
    }
    const missing = missingRequiredFields(visibleFields, values)
    if (missing.length) {
      const errors = Object.fromEntries(
        missing.map((f) => [f.name, 'This field is required.']),
      )
      setFieldErrors(errors)
      setBanner({
        tone: 'error',
        text: `${missing.length} required field${missing.length === 1 ? '' : 's'} missing.`,
      })
      const first = missing[0]
      const node = fieldRefs.current[first.name]
      if (node?.scrollIntoView) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' })
        node.focus?.()
      }
      return
    }
    setFieldErrors({})
    setExporting(true)
    setBanner(null)
    setError(null)
    try {
      const result = await exportDealDocuments({
        fieldValues: values,
        forms: selectedForms,
        toggles,
      })
      clearDealsDraft()
      setDraftOffer(null)
      setBanner({
        tone: 'ok',
        text: result.warnings?.length
          ? `Downloaded ${result.filename}. Warnings: ${result.warnings.join(' · ')}`
          : `Downloaded ${result.filename}`,
      })
    } catch (err) {
      setBanner({ tone: 'error', text: err.message || 'Export failed' })
    } finally {
      setExporting(false)
    }
  }

  const exportDisabled =
    exporting || loading || missingTemplates.length > 0 || !hasFormSelected

  return (
    <div className="space-y-6 pb-28">
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">Deals</h1>
      </header>

      <DealCard title="">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p>
            If there is a furnishing entity, send to legal to revise language.
          </p>
        </div>
      </DealCard>

      {draftOffer && !draftDismissed ? (
        <div className="flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Restore unsaved draft from {formatDraftTime(draftOffer.savedAt)}?
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={restoreDraft}
              className="rounded-lg bg-[var(--encore-blue)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Restore draft
            </button>
            <button
              type="button"
              onClick={dismissDraft}
              className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-sm font-medium text-blue-900 hover:bg-blue-100/50"
            >
              Discard
            </button>
          </div>
        </div>
      ) : null}

      {banner ? (
        <div
          className={[
            'rounded-xl border px-4 py-3 text-sm',
            banner.tone === 'error'
              ? 'border-rose-200 bg-rose-50 text-rose-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-900',
          ].join(' ')}
          role={banner.tone === 'error' ? 'alert' : 'status'}
        >
          {banner.text}
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          role="alert"
        >
          {error}
        </div>
      ) : null}

      {missingTemplates.length ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p>
            Deal templates aren&apos;t configured on this server. Contact ops to
            install the confidential Word templates.
          </p>
          <details className="mt-2 text-xs text-amber-800">
            <summary className="cursor-pointer font-medium">Technical details</summary>
            <p className="mt-1">
              Missing: {missingTemplates.map((t) => t.label || t.kind).join(', ')}.
              Templates live on the host under{' '}
              <code className="rounded bg-white px-1">deal_forms/templates/</code> and are
              not shipped in git or Docker images.
            </p>
          </details>
        </div>
      ) : null}

      <DealCard title="Deal Type">
        <fieldset>
          <legend className="sr-only">Deal forms</legend>
          <p className="mb-3 text-sm text-gray-600">
            <strong>Distribution Proposal + Longform</strong> templates, or{' '}
            <strong>Frontline Proposal</strong> template (legal must provide{' '}
            <strong>Frontline Longform</strong>).
          </p>
          <div className="space-y-3">
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                Distribution
              </p>
              <div className="flex flex-wrap gap-2">
                <FormChip
                  label="Distribution Proposal"
                  selected={forms.proposal}
                  onClick={() => toggleForm('proposal')}
                />
                <FormChip
                  label="Distribution Longform"
                  selected={forms.longform}
                  onClick={() => toggleForm('longform')}
                />
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                Frontline
              </p>
              <div className="flex flex-wrap gap-2">
                <FormChip
                  label="Frontline Proposal"
                  selected={forms.frontline_proposal}
                  onClick={() => toggleForm('frontline_proposal')}
                />
              </div>
            </div>
          </div>
        </fieldset>
      </DealCard>

      <DealCard title="Deal Options">
        <fieldset>
          <legend className="sr-only">Deal Options</legend>
          <div className="space-y-5">
            {(['distribution', 'longform', 'proposal']).map((scope) => {
              const items = togglesByScope[scope]
              if (!items?.length) return null
              return (
                <div key={scope}>
                  {scope !== 'distribution' ? (
                    <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                      {TOGGLE_SCOPE_LABELS[scope]}
                    </h3>
                  ) : null}
                  <div className="grid gap-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {items.map((t) => (
                      <OptionToggleRow
                        key={t.id}
                        toggleDef={t}
                        toggles={toggles}
                        toggleDefsById={toggleDefsById}
                        onChange={setToggle}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </fieldset>
      </DealCard>

      <DealCard title="Deal Terms">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-gray-400">
            <span className="text-rose-600">*</span> Required
          </span>
          {loading && !schema ? (
            <span className="text-xs text-gray-400">Loading fields…</span>
          ) : null}
        </div>

        {loading && !schema ? <FieldSkeletonGrid /> : null}

        {!loading && schema && !visibleFields.length ? (
          <div className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
            No fields for the current form and option combination. Select a deal type
            above.
          </div>
        ) : null}

        {!loading && visibleFields.length ? (
          <div className="space-y-8">
            {fieldGroups.map((group) => {
              const groupMissing = missingRequiredFields(group.fields, values).length
              return (
                <fieldset key={group.id} className="space-y-4">
                  <legend className="flex w-full flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-800">{group.title}</span>
                    {groupMissing > 0 ? (
                      <span className="text-xs font-normal text-rose-600">
                        {groupMissing} required field{groupMissing === 1 ? '' : 's'} empty
                      </span>
                    ) : (
                      <span className="text-xs font-normal text-gray-400">Complete</span>
                    )}
                  </legend>
                  <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
                    {group.fields.map((field) => (
                      <DealFieldInput
                        key={field.name}
                        field={field}
                        noLegal={noLegal}
                        value={values[field.name]}
                        error={fieldErrors[field.name]}
                        inputHint={fieldInputHint(field.name, { forms, toggles })}
                        inputRef={(el) => {
                          fieldRefs.current[field.name] = el
                        }}
                        onChange={(e) => updateField(field.name, e.target.value)}
                        onResetDefault={() => updateField(field.name, field.default || '')}
                      />
                    ))}
                  </div>
                </fieldset>
              )
            })}
          </div>
        ) : null}
      </DealCard>

      <div
        className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] backdrop-blur md:px-8"
        role="region"
        aria-label="Export deal documents"
      >
        <div className="mx-auto flex max-w-[1800px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 text-sm text-gray-600">
            <p className="font-medium text-gray-900">{exportSummary}</p>
            {exportPreview ? (
              <p className="truncate text-xs text-gray-500">
                Will download: <span className="font-mono">{exportPreview}</span>
              </p>
            ) : null}
            {exportDisabledReason ? (
              <p className="text-xs text-amber-700">{exportDisabledReason}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={exportDisabled}
            title={exportDisabledReason || undefined}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exporting ? 'Exporting…' : 'Export documents'}
          </button>
        </div>
      </div>
    </div>
  )
}
